import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../ui/dialog";
import React, { useEffect, useState } from "react";
import Button from "../../form/button";
import ErrorMessage from "../../form/errorMessage";
import { IFolder, IFolderRecord, Status } from "../../../lib/types";
import { Input } from "../../ui/input";
import pb from "../../../lib/pocketbase";

interface NewFolderProps {
    folder?: IFolderRecord;
    folders?: IFolderRecord[];
    onSubmit: (id: string | undefined) => void;
    movingFolder?: string;
}

export default function MoveTo({ folder, folders, onSubmit, movingFolder }: NewFolderProps) {
    const [open, setOpen] = useState(false);
    const [error, setError] = useState("");
    const [status, setStatus] = useState<Status>("none");
    const [currentFolder, setCurrentFolder] = useState(folder);
    const [subFolders, setSubFolders] = useState(folders);

    async function loadSubFolders() {
        if (currentFolder === undefined) {
            const f: IFolderRecord[] = await pb.collection("folder").getFullList({
                filter: `parent=null&&user.id="${pb.authStore.model?.id}"`,
                sort: "name",
                expand: "parent"
            });
            setSubFolders(f);
            return;
        }
        const f: IFolderRecord[] = await pb.collection("folder").getFullList({
            filter: `parent.id="${currentFolder.id}"`,
            sort: "name",
            expand: "parent"
        });
        setSubFolders(f);
    }

    async function changeFolder(id: string | undefined) {
        if (!id) {
            setCurrentFolder(undefined);
            return;
        }
        const f: IFolderRecord = await pb.collection("folder").getOne(id, {
            expand: "parent"
        });
        setCurrentFolder(f);
    }

    useEffect(() => {
        loadSubFolders();
    }, [currentFolder]);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <div className="flex cursor-pointer items-center gap-3 px-4 py-2 hover:opacity-80">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7.5L7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5" />
                    </svg>
                    Move
                </div>
            </DialogTrigger>
            <DialogContent className="mt-8 md:mt-0">
                <DialogHeader>
                    <DialogTitle>Move</DialogTitle>
                    <DialogDescription>Where do you want to move this to?</DialogDescription>
                </DialogHeader>
                <ErrorMessage error={error} close={() => setError("")} />
                {!!currentFolder && (
                    <Button onClick={() => changeFolder(currentFolder.parent)} variant={"ghost"} size={"ghost"} className="flex w-full items-center justify-between rounded-lg bg-slate-100 px-6 py-4 active:scale-95 dark:bg-slate-800">
                        <div className="flex items-center gap-4">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                            </svg>

                            <span className="font-semibold">..</span>
                        </div>
                    </Button>
                )}
                {subFolders?.map((folder) => (
                    <React.Fragment key={folder.id}>
                        {folder.id !== movingFolder && (
                            <Button onClick={() => changeFolder(folder.id)} variant={"ghost"} size={"ghost"} className="flex w-full items-center justify-between rounded-lg bg-slate-100 px-6 py-4 active:scale-95 dark:bg-slate-800">
                                <div className="flex items-center gap-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z"
                                        />
                                    </svg>
                                    <span className="font-semibold">{folder.name}</span>
                                </div>
                            </Button>
                        )}
                    </React.Fragment>
                ))}
                {currentFolder?.id !== folder?.id && (
                    <p className="italic text-slate-500 dark:text-slate-600">
                        Currently selected: <span className="font-bold">{currentFolder ? currentFolder?.name : "Start page"}</span>
                    </p>
                )}
                <DialogFooter className="gap-2">
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    {currentFolder?.id !== folder?.id && (
                        <Button
                            status={status}
                            onClick={() => {
                                onSubmit(currentFolder?.id);
                                setOpen(false);
                            }}
                            variant="dark">
                            {status === "loading" && "Moving"}
                            {status === "success" && "Moved"}
                            {status === "error" && "Error"}
                            {status === "none" && "Move to here"}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
